import { useAuthStore } from '@/stores/authStore';
import { clinicPickerHref, TENANT } from '@/routing/tenant';

export type ApiEnvelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string; details?: unknown } };

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Body shapes that `fetch` accepts as-is. Anything else is JSON-encoded.
type PassthroughBody = FormData | Blob | ArrayBuffer | URLSearchParams | ReadableStream | string;

function isPassthrough(body: unknown): body is PassthroughBody {
  return (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
  );
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /**
   * Set false for endpoints under `/api/public/*`, which anonymous visitors
   * reach with no session.
   *
   * Two behaviours are suppressed, and the second is the important one: an
   * authenticated request that 401s means the session died, so the store logs
   * out and hard-redirects to /login. On a public page there is no session to
   * lose, and that redirect would throw a patient who scanned a QR onto a staff
   * sign-in screen. Everything else — envelope unwrapping, ApiError — stays
   * shared, which is why this is a flag here rather than a second fetch wrapper.
   */
  auth?: boolean;
};

async function request<T>(url: string, opts: RequestOptions = {}): Promise<T> {
  const authenticated = opts.auth !== false;
  const { token } = useAuthStore.getState();
  const headers = new Headers(opts.headers);
  headers.set('Accept', 'application/json');

  let body: BodyInit | undefined;
  if (opts.body === undefined || opts.body === null) {
    body = undefined;
  } else if (isPassthrough(opts.body)) {
    body = opts.body;
    // FormData lets the browser set the multipart boundary; for raw strings
    // / URLSearchParams etc. the caller is responsible for Content-Type.
  } else {
    body = JSON.stringify(opts.body);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  }

  if (authenticated && token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...opts, headers, body });

  if (res.status === 204) return undefined as T;

  let envelope: ApiEnvelope<T> | null = null;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    // Left null — a 401 with no JSON body still has to end the session, which
    // the block below does before this becomes a PARSE_ERROR.
  }

  // A 401 now has exactly one meaning: the credential is gone. There used to be
  // a PERMISSIONS_STALE branch that silently re-issued the token and retried,
  // because the token carried a cached copy of the caller's permissions that the
  // server could declare out of date. Permissions are read live per request now,
  // so a token can never be stale about what its holder may do.
  if (authenticated && res.status === 401) {
    useAuthStore.getState().logout({ redirect: true });
  }

  // Two different facts, one destination. NOT_A_MEMBER means the clinic in the
  // URL is not one the caller belongs to; CLINIC_DISABLED means it is theirs but
  // a platform operator has switched it off. Neither is an error to render — the
  // session is fine — and in both cases the picker is where the answer is: it
  // lists the clinics they can actually enter, and badges the disabled one.
  //
  // This is also what closes the open-tab case: a tab sitting inside a clinic
  // that is disabled from another machine lands here on its next request rather
  // than showing a screen of failed panels.
  if (authenticated && res.status === 403 && envelope && !envelope.ok
      && (envelope.error.code === 'NOT_A_MEMBER' || envelope.error.code === 'CLINIC_DISABLED')) {
    goToClinicPicker();
  }

  if (!envelope) {
    throw new ApiError(
      'PARSE_ERROR',
      `Non-JSON response (status ${res.status})`,
      res.status,
    );
  }

  if (envelope.ok) return envelope.data;
  throw new ApiError(envelope.error.code, envelope.error.message, res.status, envelope.error.details);
}

function goToClinicPicker(): void {
  if (typeof window === 'undefined') return;
  const target = clinicPickerHref();
  if (window.location.pathname !== target) window.location.assign(target);
}

/**
 * Build the five verbs over one URL resolver.
 *
 * `api` and `clinicApi` differ in exactly one thing — how a path becomes a URL —
 * so they share everything else: the envelope, the bearer header, the 401 and
 * the NOT_A_MEMBER / CLINIC_DISABLED handling. Two named clients rather than one that infers scope
 * from a leading slash: which clinic a request is scoped to is worth being able
 * to read off the call site.
 */
function makeClient(toUrl: (path: string) => string) {
  return {
    get: <T>(path: string, opts?: RequestOptions) =>
      request<T>(toUrl(path), { ...opts, method: 'GET' }),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(toUrl(path), { ...opts, method: 'POST', body }),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(toUrl(path), { ...opts, method: 'PATCH', body }),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      request<T>(toUrl(path), { ...opts, method: 'PUT', body }),
    // DELETE returns `T | undefined` because handlers may emit 204.
    delete: <T = void>(path: string, opts?: RequestOptions) =>
      request<T | undefined>(toUrl(path), { ...opts, method: 'DELETE' }),
  };
}

/**
 * Endpoints that name no clinic: signing in, registering, listing which clinics
 * you belong to, and the public `/api/public/*` surfaces a patient reaches from
 * a QR code. Paths are absolute and used verbatim.
 */
export const api = makeClient((path) => path);

/**
 * A client scoped to a clinic named explicitly rather than taken from the URL.
 *
 * The onboarding wizard is the reason this exists: it seeds roles and sends
 * invitations for the clinic it has just created, while the page itself is still
 * in the global namespace and has no slug in its address bar.
 */
export function clinicApiFor(slug: string) {
  return makeClient((path) => `/api/c/${slug}/${path.replace(/^\/+/, '')}`);
}

/**
 * Endpoints scoped to the clinic whose slug is in the address bar. Paths are
 * relative — `clinicApi.get('patients')` becomes `/api/c/<slug>/patients`.
 *
 * The slug comes from `TENANT`, resolved once at module load from the URL, which
 * is the same value the server scopes the response by. Calling this from the
 * global namespace is a bug in the caller, not a recoverable state, so it throws
 * rather than quietly producing `/api/c/undefined/...` and a 403 to debug.
 */
export const clinicApi = makeClient((path) => {
  if (TENANT.mode !== 'tenant') {
    throw new Error(
      `clinicApi called outside a clinic namespace (path: ${path}). ` +
        'Use `api` for endpoints that are not scoped to one clinic, or ' +
        '`clinicApiFor(slug)` when the clinic is known but is not the one in the URL.',
    );
  }
  return `/api/c/${TENANT.slug}/${path.replace(/^\/+/, '')}`;
});

export const apiFetch = request;
